import { ChangeEvent, useCallback, useEffect, useRef, useState, useContext } from 'react';

import { useMediaQuery } from '@mantine/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { breakpoints } from 'styles/theme.variables';
import { formatSimpleMoney } from 'helpers/format';
import { useTranslation } from 'i18n';
import {
  MENU_OBJECT_TYPES,
  TYPE_TO_READABLE_STRING_MAP,
  MENU_NOTIFICATION_EVENT,
  MENU_NOTIFICATION_TYPE,
} from 'constants/menuManagement';
import { fetchCubohItemChildren } from 'helpers/menuManagement';
import { useMenuManagement } from 'hooks/useMenuManagement';
import {
  toAnalyticsEntityType,
  trackMenuAvailabilityToggled,
  trackMenuEntityAction,
} from 'helpers/menuAnalytics';
import { validateServingsTag, normalizeServingsTag } from 'components/MenuManagement/validation';

import {
  Accordion,
  Badge,
  Box,
  Center,
  Divider,
  Flex,
  Group,
  Image,
  Loader,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { RenderActions } from 'components/MenuManagement/MenuItemActions/RenderActions';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import {
  canEditMasterMenuItems,
  getPosSyncRowDisabledTooltip,
} from 'helpers/masterMenuPermissions';
import { logException } from 'helpers/loggers';

import {
  useHandleSuccessNotification,
  useHandleErrorNotification,
} from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';
import { ChevronDownCircleFilledIconSm } from '@chownow/pantry/icons';
import renderSubItems from './renderSubItems';
import EditableDropdown from '../EditableDropdown';
import EditablePrice from '../EditablePrice';
import { EditableText } from '../EditableText';
import EditableMinMax from '../EditableMinMax';
import { INVISIBLE_CHEVRON } from '../constants';
import { DisabledReasonTooltip } from '../DisabledReasonTooltip';
import { PosSyncLockoutIcon } from '../PosSyncLockoutIcon';
import {
  getBadgeTypeAndVariant,
  getDisabledTooltip,
  getReadableRowEntity,
  formatServingsTag,
} from './helpers';

type Classification = { classification?: string };

export type MenuItem = {
  children?: MenuItem[];
  classifications?: Classification[];
  companyId?: number;
  description?: string;
  enabled?: boolean;
  hideAddButton?: boolean;
  imageUrl?: string;
  isDefault?: boolean;
  itemType: keyof typeof TYPE_TO_READABLE_STRING_MAP;
  key?: string;
  merchantId?: number;
  maximum?: number;
  minimum?: number;
  individualMinimum?: number;
  individualMaximum?: number;
  name: string;
  noEdit?: boolean;
  ordinal?: number;
  parentType?: string;
  parentUuid?: string;
  price?: number;
  servingsTag?: string;
  seqId?: number;
  uuid: string;
};

type SubItemProps = {
  indentLevel?: number;
  item: MenuItem;
  order?: number;
  // True when this row is rendered inside a nested mod group (i.e. a descendant of a
  // mod group that is itself nested under a modifier). Threaded down so we can hide the
  // "add" affordance past the deepest supported nesting level.
  withinNestedModGroup?: boolean;
};

type ChildrenData = {
  itemType: string;
  children: MenuItem[];
};

type MenuNotificationDetail = {
  type: string;
  id?: string;
  parentUuid?: string;
};

const NS = 'menuManagementSubItem';

export function SubItem({
  indentLevel = 0,
  item,
  order,
  withinNestedModGroup = false,
}: SubItemProps) {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const { nestedModGroupAssignment } = useFlags();
  // useSuspense: false avoids suspending the page's fallback-less Suspense (CN-50159)
  const { t } = useTranslation(NS, { useSuspense: false });

  const { updateCubohMenu, getMerchantIntegrationsFromState } = useMenuManagement();
  const { menu, isCateringMenu, imageOverrides } = useContext(MenuEdit.Context);
  const handleSuccessNotification = useHandleSuccessNotification();
  const handleErrorNotification = useHandleErrorNotification();
  // useHandleErrorNotification() returns a new function every render, which would make
  // handleFetchChildren (and the effect that depends on it below) unstable too. Route calls
  // through a ref that always points at the latest notifier, so handleFetchChildren's identity
  // only changes when the item itself does.
  const handleErrorNotificationRef = useRef(handleErrorNotification);
  useEffect(() => {
    handleErrorNotificationRef.current = handleErrorNotification;
  });

  const [children, setChildren] = useState<ChildrenData | undefined>();
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [switchChecked, setSwitchChecked] = useState(item.enabled);

  // Rows are keyed by uuid, so a refetch (menu pull, accordion re-expand) reuses this instance
  // and the seeded state would keep showing a stale value. Follow the item.
  useEffect(() => {
    setSwitchChecked(item.enabled);
  }, [item.enabled]);

  const isMainItem = item.itemType === MENU_OBJECT_TYPES.items;
  const isModifierGroup = item.itemType === MENU_OBJECT_TYPES.modifierGroups;
  const isModifier = item.itemType === MENU_OBJECT_TYPES.modifiers;
  // A mod group whose parent is a modifier is itself a nested mod group.
  const isNestedModGroup = isModifierGroup && item.parentType === MENU_OBJECT_TYPES.modifiers;
  const isTopItem = isMainItem && order === 1;
  const subItemUuidSuffix = item.uuid.slice(-6);

  const hasLimitedAccess =
    !!menu?.masterMenuPermissionTier && !canEditMasterMenuItems(menu.masterMenuPermissionTier);
  const isPosSyncInPlace = !!getMerchantIntegrationsFromState(item.merchantId || '')
    ?.isPosSyncInPlace;
  const shouldDisableEdit = !!item.noEdit || hasLimitedAccess || isPosSyncInPlace;
  const disabledTooltipLabel = getDisabledTooltip({ item, hasLimitedAccess, isPosSyncInPlace });

  const handleFetchChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    try {
      const response = await fetchCubohItemChildren({
        companyID: item.companyId,
        merchantID: item.merchantId,
        parentUuid: item.uuid,
      });
      setChildren({
        itemType: response.itemType,
        children: response.items,
      });
    } catch (e) {
      const readableItemType = TYPE_TO_READABLE_STRING_MAP[item.itemType].toLowerCase();
      handleErrorNotificationRef.current({
        customMessage: `We could not load your ${readableItemType}. Please try again.`,
      });
    } finally {
      setIsLoadingChildren(false);
    }
  }, [item.companyId, item.merchantId, item.uuid, item.itemType]);

  useEffect(() => {
    const handleNotification = (event: Event) => {
      const { type, id, parentUuid } = (event as CustomEvent<MenuNotificationDetail>).detail;

      if (
        type === MENU_NOTIFICATION_TYPE.SUCCESS &&
        (id === item.uuid || parentUuid === item.uuid)
      ) {
        void handleFetchChildren();
      }
    };

    window.addEventListener(MENU_NOTIFICATION_EVENT, handleNotification);
    return () => {
      window.removeEventListener(MENU_NOTIFICATION_EVENT, handleNotification);
    };
  }, [item.uuid, handleFetchChildren]);

  const handleToggleEnable = async (event: ChangeEvent<HTMLInputElement>) => {
    const isEnabled = event.currentTarget.checked;
    // This is now the toggle path for POS-synced rows too, so a failed update has to surface -
    // updateCubohMenu rethrows, and an unhandled rejection would just snap the switch back.
    try {
      await updateCubohMenu({
        group: item.itemType,
        id: `${item.uuid}_${item.parentUuid}`,
        data: { ...item, enabled: isEnabled },
      });
    } catch (e) {
      // Nothing in here may throw, or the notification never shows: this runs from a
      // `void`-ed call, so a TypeError would surface only as an unhandled rejection.
      const readableItemType = TYPE_TO_READABLE_STRING_MAP[item.itemType]?.toLowerCase() ?? 'item';
      handleErrorNotification({ readableItemType });
      // logException replaces the error with its own message, so pass the reason via context.
      logException({
        message: 'Failed to toggle menu item availability',
        error: e,
        context: {
          reason: e instanceof Error ? e.message : String(e),
          itemType: item.itemType,
          uuid: item.uuid,
        },
        tags: null,
      });
      return;
    }

    trackMenuAvailabilityToggled({
      entityType: toAnalyticsEntityType(item.itemType),
      availability: isEnabled ? 'enabled' : 'disabled',
      menuId: menu?.uuid,
      entityId: item.uuid,
    });

    setSwitchChecked(isEnabled);
  };

  const handleOnEdit = async (textKey: string, updatedText: string) => {
    if (shouldDisableEdit) return;
    const readableItemType = TYPE_TO_READABLE_STRING_MAP[item.itemType].toLowerCase();

    try {
      const nextValue = textKey === 'servingsTag' ? normalizeServingsTag(updatedText) : updatedText;

      await updateCubohMenu({
        group: item?.itemType,
        id: item.key,
        data: { ...item, [textKey]: nextValue },
      });

      trackMenuEntityAction({
        action: 'update',
        entityType: toAnalyticsEntityType(item?.itemType),
        menuId: menu?.uuid,
        entityId: item.uuid,
        fieldsChanged: textKey,
      });

      handleSuccessNotification({ readableItemType, parentUuid: item?.parentUuid });
    } catch (e) {
      handleErrorNotification({ readableItemType });
    }
  };

  const badgeInfo = getBadgeTypeAndVariant(item.itemType);

  const hasChildren = children ? !!children?.children?.length : !!item.children?.length;

  const badgeEl = (
    <Badge
      variant={badgeInfo.variant}
      h="var(--dimension-sm)"
      style={{ overflow: 'visible' }}
      size="xs"
      w="var(--dimension-xl3)"
    >
      {badgeInfo.labelKey && t(badgeInfo.labelKey)} {order}
    </Badge>
  );

  const thumbnailSrc = imageOverrides?.[item.uuid] ?? item.imageUrl;
  // Avoids the browser's bordered broken-image glyph when there's no src.
  const imageEl =
    isMainItem &&
    (thumbnailSrc ? (
      <Image
        radius="xs2"
        mih="var(--dimension-xl)"
        miw="var(--dimension-xl3)"
        w="var(--dimension-xl3)"
        bg="var(--surface-tertiary)"
        src={thumbnailSrc}
      />
    ) : (
      <Box
        mih="var(--dimension-xl)"
        miw="var(--dimension-xl3)"
        w="var(--dimension-xl3)"
        bg="var(--surface-tertiary)"
        style={{ borderRadius: 'var(--mantine-radius-xs2)' }}
      />
    ));

  const titleEl = (
    <DisabledReasonTooltip label={disabledTooltipLabel} disabled={!shouldDisableEdit}>
      <EditableText
        rawText={item.name}
        text={<Text variant="title-sm">{item.name}</Text>}
        textKey="name"
        subText={subItemUuidSuffix}
        handleEdit={handleOnEdit}
        isRequired
        disableEdit={shouldDisableEdit}
      />
    </DisabledReasonTooltip>
  );

  // Whitespace-only descriptions exist in the data; treated as empty so the row still shows the
  // placeholder and stays clickable instead of collapsing to a blank cell (CN-51624)
  const description = item.description?.trim() ?? '';

  // Mod groups have no description; only items and modifiers carry one (CN-51624).
  const descriptionEl = (isMainItem || isModifier) && (
    <DisabledReasonTooltip
      label={disabledTooltipLabel}
      disabled={!shouldDisableEdit}
      openDelay={2000}
    >
      <EditableText
        rawText={description}
        text={
          <Text
            variant="sublabel-sm"
            lineClamp={2}
            c={!description ? 'var(--content-inverse-secondary)' : undefined}
          >
            {description || t('SubItem.description.empty')}
          </Text>
        }
        textKey="description"
        placeholder={t('SubItem.description.placeholder')}
        handleEdit={handleOnEdit}
        disableEdit={shouldDisableEdit}
      />
    </DisabledReasonTooltip>
  );

  const servingsEl = isCateringMenu && isMainItem && (
    <DisabledReasonTooltip
      label={disabledTooltipLabel}
      disabled={!shouldDisableEdit}
      openDelay={2000}
    >
      <Box w="var(--dimension-xl5)">
        <EditableText
          rawText={item.servingsTag || ''}
          text={
            <Text
              variant="sublabel-sm"
              c={!item.servingsTag ? 'var(--content-inverse-secondary)' : ''}
            >
              {formatServingsTag(item, t)}
            </Text>
          }
          textKey="servingsTag"
          handleEdit={handleOnEdit}
          disableEdit={shouldDisableEdit}
          validator={validateServingsTag}
        />
      </Box>
    </DisabledReasonTooltip>
  );

  const defaultEl = isModifier && (
    <DisabledReasonTooltip
      label={disabledTooltipLabel}
      disabled={!shouldDisableEdit}
      openDelay={2000}
    >
      <Text
        variant="sublabel-sm"
        c={!item.isDefault ? 'var(--content-inverse-secondary)' : ''}
        w="var(--dimension-xl5)"
      >
        <EditableDropdown
          displayValue={
            item.isDefault
              ? t('SubItem.modifierDefault.isDefault')
              : t('SubItem.modifierDefault.notDefault')
          }
          handleEdit={handleOnEdit}
          value={`${item.isDefault}`}
          disableEdit={shouldDisableEdit}
        />
      </Text>
    </DisabledReasonTooltip>
  );

  const minMaxEl = isModifierGroup && (
    <DisabledReasonTooltip
      label={disabledTooltipLabel}
      disabled={!shouldDisableEdit}
      openDelay={2000}
    >
      <EditableMinMax
        modifierGroup={item}
        handleEdit={handleOnEdit}
        disableEdit={shouldDisableEdit}
      />
    </DisabledReasonTooltip>
  );

  const priceEl = !isModifierGroup && (
    <DisabledReasonTooltip
      label={disabledTooltipLabel}
      disabled={!shouldDisableEdit}
      openDelay={2000}
    >
      <EditablePrice
        price={
          <Text
            variant="sublabel-sm"
            ta={isMobile ? 'left' : 'right'}
            c={!item.price ? 'var(--content-inverse-secondary)' : ''}
          >
            {!item.price ? '0.00' : formatSimpleMoney(item.price / 100)}
          </Text>
        }
        rawPrice={!item.price ? '0.00' : formatSimpleMoney(item.price / 100)}
        handleEdit={handleOnEdit}
        disableEdit={isModifierGroup || shouldDisableEdit}
      />
    </DisabledReasonTooltip>
  );

  const getShowAdd = () => {
    // A mod group's add action adds a child modifier. Nested mod groups only expose it behind
    // the flag; top-level mod groups always show it.
    if (isModifierGroup) {
      return (
        !item.parentType || item.parentType === MENU_OBJECT_TYPES.items || nestedModGroupAssignment
      );
    }
    // Only one nesting level: hide once the modifier is nested — here (withinNestedModGroup)
    // or elsewhere in the menu (BE hideAddButton).
    if (isModifier) {
      if (!nestedModGroupAssignment) return true;
      return !(withinNestedModGroup || item.hideAddButton === true);
    }
    return true;
  };

  /* Under POS sync the row keeps its actions menu and toggle; the children lock themselves
  down (CN-50652) and the notice icon explains why. */
  const actionsEl = (
    <Flex gap="xs3" align="center" flex="0 0 auto">
      {isPosSyncInPlace && (
        <PosSyncLockoutIcon
          label={getPosSyncRowDisabledTooltip(getReadableRowEntity(item.itemType))}
        />
      )}
      <RenderActions record={item} group={item.itemType} showAdd={getShowAdd()} showEdit />
      {/* Always editable for cloned/master items, and always editable on POS-synced ones */}
      <Tooltip
        label={switchChecked ? t('SubItem.enabledToggle.hide') : t('SubItem.enabledToggle.show')}
        disabled={isMobile}
        position="top"
        w="auto"
        maw="220px"
      >
        <div>
          <Switch
            size="xs"
            checked={switchChecked}
            onChange={event => {
              void handleToggleEnable(event);
            }}
          />
        </div>
      </Tooltip>
    </Flex>
  );

  return (
    <Box>
      <Center>
        <Divider
          variant={!isTopItem ? 'dashed' : undefined}
          w={!isTopItem ? 'calc(100% - 48px)' : '100%'}
        />
      </Center>
      <Accordion
        chevronPosition="left"
        chevron={hasChildren ? <ChevronDownCircleFilledIconSm /> : INVISIBLE_CHEVRON}
        variant="separated"
        onChange={value => {
          if (value !== null) void handleFetchChildren();
        }}
      >
        <Accordion.Item
          value={item.name}
          bd="none"
          my="md"
          style={{ backgroundColor: 'transparent' }}
        >
          {isMobile ? (
            <Flex
              pr="sm"
              gap="xs"
              align="flex-start"
              style={{
                paddingLeft: `calc(var(--mantine-spacing-sm) + var(--mantine-spacing-xs2) * ${indentLevel})`,
              }}
            >
              <Accordion.Control
                style={{ opacity: '1', cursor: 'initial' }}
                w="auto"
                styles={{
                  chevron: {
                    marginInlineStart: 0,
                    marginInlineEnd: 0,
                    alignItems: 'center',
                  },
                  label: { display: 'none' },
                  control: {
                    backgroundColor: 'transparent',
                    height: 'var(--dimension-sm)',
                    padding: 0,
                    paddingTop: 'var(--mantine-spacing-xs3)',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              />
              <Stack flex={1} gap="xs" miw={0}>
                <Flex justify="space-between" align="center" gap="xs">
                  {badgeEl}
                  {actionsEl}
                </Flex>
                {imageEl}
                <Stack gap="xs3">{titleEl}</Stack>
                {/* mobile shows nothing at all when there's no description - no placeholder */}
                {description && descriptionEl}
                {(item.servingsTag || item.isDefault || isModifierGroup) && (
                  <Flex gap="md" align="center" wrap="wrap">
                    {item.servingsTag && servingsEl}
                    {item.isDefault && defaultEl}
                    {minMaxEl}
                  </Flex>
                )}
                {priceEl}
              </Stack>
            </Flex>
          ) : (
            <Flex
              direction="row"
              bg="var(--surface-primary)"
              align="center"
              gap="xs"
              pr="md"
              w="100%"
            >
              <Accordion.Control
                style={{ opacity: '1', cursor: 'initial' }}
                bg="var(--surface-primary)"
                pl={indentLevel * 24}
                pr={0}
                w="auto"
                styles={{ chevron: { marginInlineEnd: 0 }, label: { display: 'none' } }}
              />
              <Group justify="space-between" gap="xs" w="100%" wrap="nowrap">
                <Flex direction="row" align="center" gap="xs" flex={1} miw={0}>
                  {badgeEl}
                  {imageEl}
                  <Stack gap="xs3" flex="0 0 200px" miw={0}>
                    {titleEl}
                  </Stack>
                  {/* Always rendered: mod group rows have no description, but the box still
                   takes up the free space so their min/max stays right-aligned */}
                  <Box flex="1 1 0" miw={0}>
                    {descriptionEl}
                  </Box>
                  <Flex
                    flex="0 0 auto"
                    justify="flex-end"
                    align="center"
                    gap={isModifier ? 'xs' : 'sm'}
                  >
                    {servingsEl}
                    {defaultEl}
                    {minMaxEl && (
                      <Flex w="var(--dimension-xl5)" justify="flex-start">
                        {minMaxEl}
                      </Flex>
                    )}
                    {/* Always reserved so mod group rows (no price) align with Serving/Default. */}
                    <Flex w="var(--dimension-xl3)" justify="flex-end" align="center">
                      {priceEl}
                    </Flex>
                  </Flex>
                </Flex>
                {actionsEl}
              </Group>
            </Flex>
          )}
          {hasChildren && (children || isLoadingChildren) && (
            <Accordion.Panel
              styles={{
                content: {
                  marginTop: 'var(--mantine-spacing-md)',
                  paddingBottom: 0,
                },
              }}
            >
              {isLoadingChildren && (
                <Center my="md">
                  <Loader />
                </Center>
              )}
              {children &&
                renderSubItems({
                  item: children,
                  indentLevel: indentLevel + 1,
                  withinNestedModGroup: withinNestedModGroup || isNestedModGroup,
                })}
            </Accordion.Panel>
          )}
        </Accordion.Item>
      </Accordion>
    </Box>
  );
}
